import { useEffect, useState } from "react";
import api from "./api";
import { useAuth } from "../context/AuthContext";

let cached = null; // module-cache for the trainer's stored timezone
let inflight = null;

/**
 * Returns the trainer's stored IANA timezone (e.g. "Europe/London").
 * Reads from /api/profile/timezone once per session. Falls back to the
 * browser timezone, then UTC. Safe to call from many components.
 */
export function useTrainerTimezone() {
  const { user } = useAuth();
  const [tz, setTz] = useState(() => {
    if (cached) return cached;
    try { return Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC"; }
    catch (_e) { return "UTC"; }
  });

  useEffect(() => {
    if (!user?.id) return;
    if (cached) {
      setTz(cached);
      return;
    }
    if (!inflight) {
      inflight = api.get("/profile/timezone")
        .then((r) => { cached = r.data?.timezone || cached || "UTC"; return cached; })
        .catch(() => { cached = "UTC"; return cached; })
        .finally(() => { inflight = null; });
    }
    let cancelled = false;
    inflight.then((v) => { if (!cancelled && v) setTz(v); });
    return () => { cancelled = true; };
  }, [user?.id]);

  return tz;
}

/** Lightweight wall-clock formatter for a given IANA timezone. */
export function formatTimeInTz(isoOrDate, tz, opts = {}) {
  try {
    const d = isoOrDate instanceof Date ? isoOrDate : new Date(isoOrDate);
    return d.toLocaleTimeString("en-US", {
      timeZone: tz,
      hour: "numeric",
      minute: "2-digit",
      hour12: true,
      ...opts,
    }).replace(":00", "");
  } catch (_e) {
    return "—";
  }
}

/** Short tz label, e.g. "PST", "GMT", "JST" — falls back to last segment. */
export function shortTzLabel(tz) {
  if (!tz) return "";
  try {
    const fmt = new Intl.DateTimeFormat("en-US", { timeZone: tz, timeZoneName: "short" });
    const parts = fmt.formatToParts(new Date());
    const piece = parts.find((p) => p.type === "timeZoneName");
    if (piece) return piece.value;
  } catch (_e) { /* ignore */ }
  return tz.split("/").pop().replace(/_/g, " ");
}
